import * as moment from "moment";

import { ClinicLocationTypeName } from "../appointment/models";
import { ServerModelWithDateStrings } from "../utils";

export enum AppointmentStatus {
    None = "None",
    Pending = "Pending",
    DidNotAttend = "DidNotAttend",
    EmployeeCheckedIn = "EmployeeCheckedIn",
    Completed = "Completed"
}
/*
 * Appointments come in many forms, the types in this file attempts to represent these in typescript.
 * When using these types you'll often need to narrow the type down to the correct type your code is expecting to handle.
 * See: https://www.typescriptlang.org/docs/handbook/2/narrowing.html
 * - `Appointment` is the Union of all of these forms which include:
 *     - `AdHocAppointment` - An appointment that is not part of a clinic and has simply been created into a user's calendar. This can be either:
 *         - An appointment for a case, i.e. it's linked to a consultation task on a case
 *         - A time note not linked to a case, i.e. Annual leave for a user
 *     - `Clinic` - A clinic which can be one of two forms:
 *         - `FixedLengthClinic` - A clinic with child appointments of a fixed start and end time, and a fixed length slots for each child appointment
 *         - `OpenClinic` - A clinic with child appointments of no fixed length but each child appointment has a fixed start and end time
 *         - `NonSlotClinic` - A clinic where all child appointments have no fixed start and end times, i.e. a mobile clinic
 *    - `ChildAppointment` - An appointment that is part of a clinic. This can be either:
 *         - `FixedLengthChildAppointment` - A child appointment of a fixed start and end time
 *         - `NonFixedLengthChildAppointment` - A child appointment of a non-fixed start and end time
 *    - `TopLevelAppointment` - A union of `AdHocAppointment` and `Clinic` which represents the top level appointment in a users calendar
 * NOTE: Each of these types has many properties that are optional,
 * this is for legacy reasons and we should aim to tidy this up and use the most specific type possible to achieve this.
 */

export type ChildAppointment = FixedLengthChildAppointment | NonFixedLengthChildAppointment;
export type TopLevelAppointment = AdHocAppointment | Clinic;

export interface FixedLengthChildAppointment {
    id: string;
    start: moment.Moment;
    end: moment.Moment;
    ohCaseId: string;
    taskTypes?: AppointmentTaskType[];
    status: AppointmentStatus;
    clinicType?: undefined;
    clinicLocationTypes: ClinicLocationTypeName[];
    teamPreventUsers?: { id: string; name: string }[];
    rooms?: { id: string; name: string; addressLine1: string }[];
    description: string;
    bookingTypeId?: string;
    employee: { id: string; name: string };
    slotLengthInMinutes?: undefined;
    appointments?: undefined;
    baseAppointment?: boolean;
    baseAppointmentId?: string;
    customer?: { id: string; name: string };
    isClinicalNotesComplete: boolean;
    /* This is a legacy property that should be removed, it should only be present on clinics */
    cliniciansRequired?: number;
}

export interface NonFixedLengthChildAppointment {
    id: string;
    start?: undefined;
    end?: undefined;
    ohCaseId: string;
    taskTypes?: AppointmentTaskType[];
    status: AppointmentStatus;
    teamPreventUsers?: { id: string; name: string }[];
    description: string;
    bookingTypeId?: string;
    employee: { id: string; name: string };
    baseAppointment?: boolean;
    baseAppointmentId?: string;
    clinicType?: undefined;
    isClinicalNotesComplete: boolean;
    customer?: { id: string; name: string };
    appointments?: undefined;
    rooms?: { id: string; name: string; addressLine1: string }[];
    clinicLocationTypes: ClinicLocationTypeName[];
    slotLengthInMinutes?: undefined;
    /* This is a legacy property that should be removed, it should only be present on clinics */
    cliniciansRequired?: number;
}

export interface AdHocAppointment {
    id: string;
    start: moment.Moment;
    end: moment.Moment;
    clinicType?: undefined;
    clinicLocationTypes?: undefined;
    appointments?: undefined;
    slotLengthInMinutes?: undefined;
    teamPreventUsers: { id: string; name: string }[];
    cliniciansRequired?: undefined;
    ohCaseId: string;
    taskTypes?: AppointmentTaskType[];
    status: AppointmentStatus;
    rooms?: { id: string; name: string; addressLine1: string }[];
    description: string;
    bookingTypeId?: string;
    employee: { id: string; name: string };
    customer?: { id: string; name: string };
    isClinicalNotesComplete: boolean;
    baseAppointment?: false;
    baseAppointmentId?: undefined;
}

export type Clinic = FixedLengthClinic | OpenClinic | NonSlotClinic;

export interface FixedLengthClinic {
    id: string;
    start: moment.Moment;
    end: moment.Moment;
    appointments: FixedLengthChildAppointment[];
    slotLengthInMinutes: number;
    teamPreventUsers: { id: string; name: string }[];
    clinicLocationTypes: ClinicLocationTypeName[];
    clinicType: Exclude<string, "Health Surveillance">;
    cliniciansRequired: number;
    ohCaseId?: undefined;
    taskTypes?: undefined;
    status: AppointmentStatus;
    rooms?: { id: string; name: string; addressLine1: string }[];
    description: string;
    bookingTypeId?: string;
    employee?: undefined;
    customer?: { id: string; name: string };
    customerIds?: string[];
    baseAppointment?: false;
    baseAppointmentId?: undefined;
    billableByType: string;
    billableCustomerIds: string[];
    billableOrgStructure?: { id: string; name: string };
    isClinicalNotesComplete?: undefined;
}

export interface NonSlotClinic {
    id: string;
    start: moment.Moment;
    end: moment.Moment;
    appointments: NonFixedLengthChildAppointment[];
    slotLengthInMinutes?: undefined;
    teamPreventUsers: { id: string; name: string }[];
    clinicLocationTypes: ClinicLocationTypeName[];
    clinicType: "Health Surveillance";
    cliniciansRequired: number;
    ohCaseId?: undefined;
    taskTypes?: undefined;
    status: AppointmentStatus;
    rooms?: { id: string; name: string; addressLine1: string }[];
    description: string;
    bookingTypeId?: string;
    employee?: undefined;
    customer?: { id: string; name: string };
    customerIds?: string[];
    baseAppointment?: false;
    baseAppointmentId?: undefined;
    billableByType: string;
    billableCustomerIds: string[];
    billableOrgStructure?: { id: string; name: string };
    isClinicalNotesComplete?: undefined;
}

export interface OpenClinic {
    id: string;
    start: moment.Moment;
    end: moment.Moment;
    appointments: FixedLengthChildAppointment[];
    slotLengthInMinutes?: undefined;
    teamPreventUsers: { id: string; name: string }[];
    clinicLocationTypes: ClinicLocationTypeName[];
    clinicType: Exclude<string, "Health Surveillance">;
    cliniciansRequired: number;
    ohCaseId?: undefined;
    taskTypes?: undefined;
    status: AppointmentStatus;
    rooms?: { id: string; name: string; addressLine1: string }[];
    description: string;
    bookingTypeId?: string;
    employee?: undefined;
    customer?: { id: string; name: string };
    customerIds?: string[];
    baseAppointment?: false;
    baseAppointmentId?: undefined;
    billableByType: string;
    billableCustomerIds: string[];
    billableOrgStructure?: { id: string; name: string };
    isClinicalNotesComplete?: undefined;
}

export type Appointment = AdHocAppointment | Clinic | ChildAppointment;

export type AppointmentAndParent = (ChildAppointment & { parentAppointment: Clinic }) | ((AdHocAppointment | Clinic) & { parentAppointment?: undefined });

export interface AppointmentTaskType {
    type: string;
    description: string;
}

export interface WorkingHours {
    workingDays: number[];
    workingDayStartTime: string;
    workingDayEndTime: string;
}

export interface CalendarDetails {
    entityId: string;
    entityName: string;
    entitySubHeading?: string;
    appointments: Appointment[];
    workingHours: WorkingHours;
    hasAnySlotGaps?: boolean;
    hasAnyClinics?: boolean;
}

export interface TimePeriod {
    start: moment.Moment;
    end: moment.Moment;
}

export const APPOINTMENT_DATA_FORMAT = "appointment";

export type Direction = "horizontal" | "vertical";

export interface GapDuration {
    gapInMinutes: string;
    text: string;
}

export const GapDurations: GapDuration[] = [
    { gapInMinutes: "10", text: "10" },
    { gapInMinutes: "15", text: "15" },
    { gapInMinutes: "30", text: "30" },
    { gapInMinutes: "45", text: "45" },
    { gapInMinutes: "60", text: "60" },
    { gapInMinutes: "9999", text: ">60" },
];

export const mapToAppointmentModel = <T extends Appointment>(appointment: T): T => {
    return {
        ...appointment,
        start: appointment.start && moment.utc(appointment.start),
        end: appointment.end && moment.utc(appointment.end),
        appointments: appointment.appointments && appointment.appointments.map(mapToAppointmentModel)
    };
};

/**
 * @deprecated We should replace this with a mapper which can correctly map the various types of appointment types it may encounter rather than casting
 * @param appointment The appointment object returned from the server
 * @returns The appointment object with the start and end dates converted to moment objects
 */
export const mapServerModelToAppointmentModel = (appointment: ServerModelWithDateStrings<Appointment>): Appointment => {
    return {
        ...appointment,
        start: appointment.start ? moment.utc(appointment.start) : null,
        end: appointment.end ? moment.utc(appointment.end) : null,
        appointments: appointment.appointments
            && (appointment.appointments as ServerModelWithDateStrings<ChildAppointment>[]).map(mapServerModelToAppointmentModel)
    } as Appointment;
};

export const getAppointmentsAndEmptySlots = (appointment: Appointment): (
    (ChildAppointment & { emptySlot?: false }) | (Partial<ChildAppointment> & { emptySlot: true })
)[] => {
    const { start, end, slotLengthInMinutes, appointments } = appointment;
    if (!appointments) {
        return [];
    }

    if (!slotLengthInMinutes) {
        return appointments;
    }

    const appointmentsAndEmptySlots = appointments.map<
        (ChildAppointment & { emptySlot?: false }) | (Partial<ChildAppointment> & { emptySlot: true })
            >(app => ({ ...app, emptySlot: false }));
    let nextSlotStartTime = start;
    let nextSlotEndTime = nextSlotStartTime.clone().add(slotLengthInMinutes, "minutes");
    while (nextSlotEndTime.isSameOrBefore(end)) {
        if (!appointments.find(app => app.start.isBefore(nextSlotEndTime) && app.end.isAfter(nextSlotStartTime))) {
            appointmentsAndEmptySlots.push(
                {
                    start: nextSlotStartTime,
                    end: nextSlotEndTime,
                    description: "",
                    emptySlot: true,
                    cliniciansRequired: 0,
                    teamPreventUsers: appointment.teamPreventUsers,
                    clinicLocationTypes: appointment.clinicLocationTypes,
                    isClinicalNotesComplete: false,
                    status: AppointmentStatus.None
                }
            );
        }
        nextSlotStartTime = nextSlotStartTime.clone().add(slotLengthInMinutes, "minutes");
        nextSlotEndTime = nextSlotStartTime.clone().add(slotLengthInMinutes, "minutes");
    }

    return appointmentsAndEmptySlots;
};

export const filterAppointsBasedOnGapLength = (
    appointment: Appointment,
    minimumGapLengthInMinutes: number = Number.NEGATIVE_INFINITY): boolean => {
    const { start, end, slotLengthInMinutes, appointments } = appointment;

    if (!appointments || !slotLengthInMinutes || minimumGapLengthInMinutes > slotLengthInMinutes) {
        return false;
    }

    const totalClinicDuration = end.clone().diff(start, "minutes");
    const appointmentCount = totalClinicDuration / slotLengthInMinutes;

    return appointmentCount !== appointments.length;
};

export const isNonSlotClinicAvailable = (
    appointment: Appointment,
    minimumGapLengthInMinutes?: number): boolean => {
    const { start, end, appointments, clinicLocationTypes } = appointment;

    if (!start || !end) {
        return false;
    }

    const totalClinicDuration = end.clone().diff(start, "minutes");
    minimumGapLengthInMinutes = minimumGapLengthInMinutes > 60 ? 61 : minimumGapLengthInMinutes;
    const countOfGabsWithMinLength = (totalClinicDuration / minimumGapLengthInMinutes);

    if (appointments.length === 0 && countOfGabsWithMinLength >= 1)
    {
        return true;
    }

    if (clinicLocationTypes.indexOf(ClinicLocationTypeName.mobile) > -1) {
        // #73073 - There are no time restrictions for booking appointments on mobile clinics,
        // so if the duration is valid as checked above, the clinic should be deemed as having a gap.
        return true;
    }

    const sortedAppointments = appointments.sort((a, b) => a.start.diff(b.start));
    const appointmentCount = appointments.length;

    const appointmentSlotsValid = sortedAppointments.map((a, index) => {
        const lastAppointment = appointmentCount === index + 1;

        if (index === 0) {

            const differenceFromClinicStart = a.start.clone().diff(start, "minutes");
            let differenceFromClinicEnd = end.clone().diff(a.end, "minutes");

            if (lastAppointment && differenceFromClinicEnd >= minimumGapLengthInMinutes) {
                return true;
            }

            if (differenceFromClinicStart >= minimumGapLengthInMinutes) {
                return true;
            }

            const nextAppointment = lastAppointment ? end : sortedAppointments[index + 1].start;
            differenceFromClinicEnd = nextAppointment.diff(a.end.clone(), "minutes");

            return differenceFromClinicEnd >= minimumGapLengthInMinutes;
        }

        const nextAppointment = lastAppointment ? end : sortedAppointments[index + 1].start;
        const differenceFromClinicEnd = nextAppointment.diff(a.end.clone(), "minutes");

        return differenceFromClinicEnd >= minimumGapLengthInMinutes;
    });

    return appointmentSlotsValid.some(s => s);
};
